import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarDays, MapPin, MessageSquare, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { usePortal } from '../PortalContext';
import { usePortalJob } from '../hooks/usePortalJobs';
import { usePortalEstimates } from '../hooks/usePortalEstimates';
import { usePortalInvoices } from '../hooks/usePortalInvoices';
import { invoiceBalance } from '../hooks/portalClient';
import { estimateStatus, invoiceStatus, jobStatus } from '../status';
import { PortalStatusBadge } from '../components/PortalStatusBadge';
import { PortalListRow } from '../components/PortalListRow';
import { PortalSection } from '../components/PortalSection';
import { PortalLoading } from '../components/PortalLoading';
import { PortalErrorState } from '../components/PortalErrorState';

/** "08:30:00" → "8:30 am" */
function formatTime(value: string | null): string | null {
  if (!value) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(value);
  if (!m) return value;
  const h = Number(m[1]);
  const suffix = h >= 12 ? 'pm' : 'am';
  return `${h % 12 || 12}:${m[2]} ${suffix}`;
}

export default function PortalJobDetail() {
  const { id } = useParams<{ id: string }>();
  const { slug, company, timezone, formatMoney, formatDate, formatShortDate } = usePortal();
  const query = usePortalJob(company.id, id);
  const estimatesQ = usePortalEstimates(company.id);
  const invoicesQ = usePortalInvoices(company.id);

  if (query.isLoading) return <PortalLoading label="Loading job…" />;
  if (query.error) return <PortalErrorState onRetry={() => void query.refetch()} />;
  const job = query.data ?? null;
  if (!job) return <PortalErrorState title="Job not found" message="This job isn't available on your account." />;

  const status = jobStatus(job.status);
  const start = formatTime(job.scheduled_time_start);
  const end = formatTime(job.scheduled_time_end);
  const timeWindow = start && end ? `${start} – ${end}` : start;
  const address = [job.customer_address, job.customer_suburb, job.customer_state, job.customer_postcode]
    .map((s) => (s ?? '').trim())
    .filter(Boolean)
    .join(', ');

  const quote = job.lead_id ? (estimatesQ.data ?? []).find((e) => e.lead_id === job.lead_id) ?? null : null;
  const invoice = (invoicesQ.data ?? []).find((i) => i.job_id === job.id) ?? null;

  return (
    <div className="space-y-4">
      <Button asChild variant="ghost" size="sm" className="-ml-2 gap-1.5">
        <Link to={`/portal/${slug}/home`}><ArrowLeft className="h-4 w-4" /> Back to your account</Link>
      </Button>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm text-muted-foreground">Job {job.job_number}</p>
              <p className="text-xl font-semibold">{job.service_type_name || 'Service call'}</p>
            </div>
            <PortalStatusBadge status={status} />
          </div>

          <div className="mt-4 space-y-2 text-sm">
            {(job.scheduled_date || job.job_date) && (
              <p className="flex items-start gap-2">
                <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span>
                  {formatDate(job.scheduled_date ?? job.job_date)}
                  {timeWindow ? <span className="text-muted-foreground"> · {timeWindow}</span> : null}
                </span>
              </p>
            )}
            {address && (
              <p className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span>{address}</span>
              </p>
            )}
            {job.tech_first_name && (
              <p className="flex items-start gap-2">
                <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span>Your technician: {job.tech_first_name}</span>
              </p>
            )}
          </div>

          {job.issue_description?.trim() && (
            <>
              <Separator className="my-4" />
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Your request</p>
              <p className="mt-1 whitespace-pre-wrap text-sm">{job.issue_description.trim()}</p>
            </>
          )}
          {job.work_summary?.trim() && (
            <>
              <Separator className="my-4" />
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Work performed</p>
              <p className="mt-1 whitespace-pre-wrap text-sm">{job.work_summary.trim()}</p>
            </>
          )}

          <Separator className="my-4" />
          <Button asChild variant="outline" size="sm" className="gap-1.5">
            <Link to={`/portal/${slug}/message?job=${job.id}`}>
              <MessageSquare className="h-4 w-4" /> Message us about this job
            </Link>
          </Button>
        </CardContent>
      </Card>

      {(quote || invoice) && (
        <PortalSection title="Related">
          {quote && (
            <PortalListRow
              to={`/portal/${slug}/estimates/${quote.id}`}
              title={`Quote ${quote.estimate_number}`}
              date={formatShortDate(quote.sent_at ?? quote.created_at)}
              description={formatMoney(quote.estimate_amount)}
              status={estimateStatus(quote, timezone)}
            />
          )}
          {invoice && (
            <PortalListRow
              to={`/portal/${slug}/invoices/${invoice.id}`}
              title={`Invoice ${invoice.invoice_number}`}
              date={formatShortDate(invoice.issue_date ?? invoice.created_at)}
              description={invoiceBalance(invoice) > 0 && invoice.status !== 'cancelled'
                ? `${formatMoney(invoiceBalance(invoice))} outstanding`
                : formatMoney(invoice.total_amount)}
              status={invoiceStatus(invoice, formatShortDate)}
            />
          )}
        </PortalSection>
      )}
    </div>
  );
}
